/**
 * 一个批量任务的**管理动作**（暂停 / 恢复 / 结束 / 删除）。
 *
 * ## 为什么单独一个组件
 *
 * 2026-10-29 用户要求把任务挪到「听写历史」下面之后，任务的管理按钮就跟着
 * 搬到了**历史列表的组头**上（原来在「批量任务」页签的列表里，那个列表已经删了）。
 * 而"组头"是一个会被复用的位置（历史分组、以后别处也可能用），所以抽出来 ——
 * 免得同一套 confirm 文案与失效逻辑写两遍、两处说法不一致。
 *
 * ## 语义（与 `dictation-assignments.service.ts` 一一对应）
 *
 * | 动作 | 状态 | 孩子端 |
 * | --- | --- | --- |
 * | 暂停 | `paused` | 点「开始听写」会被拒（"家长暂时停下了"），进度全在 |
 * | 恢复 | `active` | 能继续听 |
 * | 结束 | `finished` | 不再发新的一批；**记录与错词都保留** |
 * | 重新开始 | `active` | 误点结束能救回来 |
 * | 删除 | — | 只删这份计划；已听过的记录变成"独立的一次听写"（外键 SET NULL） |
 *
 * ⚠️ 学生端**没有**这些按钮（后端 `canManageDictationAssignment` 对学生恒 false）。
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';
import { IconCheck, IconPause, IconPencil, IconPlay, IconRotateCcw, IconTrash } from '../ui/Icons';
import { toErrorMessage } from '../../lib/api/error';
import * as assignmentApi from '../../lib/api/dictationAssignments';
import { AssignmentEditModal } from './AssignmentEditModal';

export interface AssignmentAdminActionsProps {
  studentId: string;
  /** 历史组头里只有摘要（标题 / 状态 / 进度）—— 编辑弹窗会自己去取完整任务。 */
  assignment: Pick<assignmentApi.AssignmentView, 'id' | 'title' | 'status'>;
  /** 只有 owner/editor 家长能用；`false` 时**整块不渲染**（不渲染点了会失败的按钮）。 */
  canEdit: boolean;
}

export function AssignmentAdminActions({
  studentId,
  assignment,
  canEdit,
}: AssignmentAdminActionsProps) {
  const toast = useToast();
  const qc = useQueryClient();

  /** 「编辑」弹窗（标题 / 每次几个 / 顺序 / 节奏；范围不可改，见弹窗里的说明）。 */
  const [editing, setEditing] = useState(false);

  /** 任务的任何变化都要让「历史」与「批量任务」两处一起失效。 */
  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ['dictation-assignments', studentId] });
    await qc.invalidateQueries({ queryKey: ['dictation-sessions', studentId] });
  };

  const statusMutation = useMutation({
    mutationFn: (status: assignmentApi.AssignmentStatusView) =>
      assignmentApi.updateAssignment(studentId, assignment.id, { status }),
    onSuccess: async (updated) => {
      toast.showToast(
        updated.status === 'paused'
          ? '已暂停这个任务（孩子暂时开始不了，进度都还在）'
          : updated.status === 'finished'
            ? '已结束这个任务（已听过的记录与错词都保留）'
            : '已恢复这个任务',
        'success',
      );
      await invalidate();
    },
    onError: (err: unknown) => toast.showToast(`改状态失败：${toErrorMessage(err)}`, 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => assignmentApi.deleteAssignment(studentId, assignment.id),
    onSuccess: async (result) => {
      toast.showToast(
        result.discardedBatches > 0
          ? `已删除这个任务（顺手清掉了 ${result.discardedBatches} 批还没交的）；已经听过的记录都还在`
          : '已删除这个任务（已经听过的记录都还在）',
        'success',
      );
      await invalidate();
    },
    onError: (err: unknown) => toast.showToast(`删除失败：${toErrorMessage(err)}`, 'error'),
  });

  if (!canEdit) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {/*
        「编辑」：标题 / 每次几个 / 顺序 / 节奏（**范围不可改**，理由见弹窗）。
        用户原话："听写任务要有编辑修改功能：可以改的就放开更改，不能改的就不能改。"
      */}
      <Button
        size="sm"
        variant="secondary"
        icon={<IconPencil size={14} />}
        onClick={() => setEditing(true)}
      >
        编辑
      </Button>

      {assignment.status === 'active' ? (
        <Button
          size="sm"
          variant="secondary"
          icon={<IconPause size={14} />}
          loading={statusMutation.isPending}
          onClick={() => statusMutation.mutate('paused')}
        >
          暂停
        </Button>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          icon={<IconPlay size={14} />}
          loading={statusMutation.isPending}
          onClick={() => statusMutation.mutate('active')}
        >
          {assignment.status === 'finished' ? '重新开始' : '恢复'}
        </Button>
      )}

      {assignment.status !== 'finished' && (
        <Button
          size="sm"
          variant="secondary"
          icon={<IconCheck size={14} />}
          loading={statusMutation.isPending}
          onClick={() => {
            if (
              window.confirm(
                `结束「${assignment.title}」吗？结束后不再发新的一批，但已经听过的记录和错词都会保留。`,
              )
            ) {
              statusMutation.mutate('finished');
            }
          }}
        >
          结束
        </Button>
      )}

      <Button
        size="sm"
        variant="ghost"
        aria-label={`删除 ${assignment.title}`}
        title="删除这个任务"
        icon={deleteMutation.isPending ? <IconRotateCcw size={14} /> : <IconTrash size={14} />}
        loading={deleteMutation.isPending}
        onClick={() => {
          if (
            window.confirm(
              `删除「${assignment.title}」吗？\n\n已经听过的批会保留（变成「独立听写」）；还没交的那一批会一起清掉。这份计划删了不能恢复。`,
            )
          ) {
            deleteMutation.mutate();
          }
        }}
      >
        删除
      </Button>

      <AssignmentEditModal
        open={editing}
        studentId={studentId}
        assignmentId={assignment.id}
        fallbackTitle={assignment.title}
        onClose={() => setEditing(false)}
      />
    </div>
  );
}
